import { AxiosError } from "axios";
import React from "react";
import { useQueryClient } from "react-query";
import { Button, ButtonVariants } from "../../common/componentLibrary";
import { QueryKeys } from "../constants/QueryKeys";
import {
    confirmPassword,
    confirmTwoFactor,
    enableTwoFactor,
    getTwoFactorQrCode,
    getTwoFactorRecoveryCodes,
    getTwoFactorSecretKey,
    getUser,
} from "../queries";

export interface ITwoFactorEnrollmentProps {
    completeLabel: string;
    onCompleted: () => void;
}

export function TwoFactorEnrollment({ completeLabel, onCompleted }: ITwoFactorEnrollmentProps) {
    const queryClient = useQueryClient();
    const [step, setStep] = React.useState<"password" | "confirm" | "recovery">("password");
    const [password, setPassword] = React.useState("");
    const [code, setCode] = React.useState("");
    const [qrSvg, setQrSvg] = React.useState("");
    const [secretKey, setSecretKey] = React.useState("");
    const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    const [busy, setBusy] = React.useState(false);

    async function onConfirmPassword(event: React.FormEvent) {
        event.preventDefault();
        setError(null);
        setBusy(true);

        try {
            await confirmPassword(password);
            await enableTwoFactor();
            const [qr, secret] = await Promise.all([getTwoFactorQrCode(), getTwoFactorSecretKey()]);
            setQrSvg(qr.svg);
            setSecretKey(secret);
            setStep("confirm");
        } catch (setupError) {
            const status = (setupError as AxiosError).response?.status;

            setError(status === 422 ? "Email and password combination is incorrect." : "Could not start two-factor setup. Try again.");
        } finally {
            setBusy(false);
        }
    }

    async function onConfirmCode(event: React.FormEvent) {
        event.preventDefault();
        setError(null);
        setBusy(true);

        try {
            await confirmTwoFactor(code);
            const codes = await getTwoFactorRecoveryCodes();
            setRecoveryCodes(codes);
            setStep("recovery");
        } catch (confirmError) {
            const status = (confirmError as AxiosError).response?.status;

            setError(status === 422 ? "The provided two factor authentication code was invalid." : "Could not confirm authenticator.");
        } finally {
            setBusy(false);
        }
    }

    async function onComplete() {
        setError(null);
        setBusy(true);

        try {
            // Write to the cache ourselves rather than going through `fetchQuery`: a failure
            // there would put the shared auth query into an error state and tear this screen
            // down, taking the recovery codes with it. They are only ever shown once.
            queryClient.setQueryData(QueryKeys.Authenticate, await getUser());
        } catch (refreshError) {
            // The button stays on screen, so clicking it again is the retry.
            setError("Two-factor authentication is on, but we could not refresh your session. Copy your recovery codes, then try again.");

            return;
        } finally {
            setBusy(false);
        }

        onCompleted();
    }

    return (
        <React.Fragment>
            {step === "password" && (
                <form className="space-y-6" onSubmit={onConfirmPassword}>
                    <p className="text-sm text-gray-600">Confirm your password to enable an authenticator app.</p>
                    <div>
                        <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                            Password
                        </label>
                        <input
                            id="confirm-password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    {error && <div className="text-sm text-red-700">{error}</div>}
                    <Button as="button" type="submit" variant={ButtonVariants.Primary} className="w-full justify-center" disabled={busy}>
                        Continue
                    </Button>
                </form>
            )}

            {step === "confirm" && (
                <form className="space-y-6" onSubmit={onConfirmCode}>
                    <p className="text-sm text-gray-600">Scan this QR code with your authenticator app, then enter a code.</p>
                    {qrSvg && <div className="flex justify-center" data-testid="two-factor-qr" dangerouslySetInnerHTML={{ __html: qrSvg }} />}
                    {secretKey && (
                        <p className="text-xs text-center text-gray-500 break-all">
                            Secret key: <span data-testid="two-factor-secret">{secretKey}</span>
                        </p>
                    )}
                    <div>
                        <label htmlFor="setup-code" className="block text-sm font-medium text-gray-700">
                            Authentication code
                        </label>
                        <input
                            id="setup-code"
                            value={code}
                            onChange={(event) => setCode(event.target.value)}
                            autoComplete="one-time-code"
                            required
                            placeholder="123456"
                            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    {error && <div className="text-sm text-red-700">{error}</div>}
                    <Button as="button" type="submit" variant={ButtonVariants.Primary} className="w-full justify-center" disabled={busy}>
                        Confirm
                    </Button>
                </form>
            )}

            {step === "recovery" && (
                <div className="space-y-6">
                    <p className="text-sm text-gray-600">
                        Store these recovery codes in a safe place. Each code can be used once if you lose your authenticator.
                    </p>
                    <ul className="font-mono text-sm bg-gray-50 rounded-md p-4 space-y-1">
                        {recoveryCodes.map((recoveryCode) => (
                            <li key={recoveryCode}>{recoveryCode}</li>
                        ))}
                    </ul>
                    {error && <div className="text-sm text-red-700">{error}</div>}
                    <Button
                        as="button"
                        type="button"
                        variant={ButtonVariants.Primary}
                        className="w-full justify-center"
                        disabled={busy}
                        onClick={onComplete}>
                        {completeLabel}
                    </Button>
                </div>
            )}
        </React.Fragment>
    );
}
